import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SimpleShowLayout, Title } from 'react-admin';

import { GlobalFeatureFlagsPanel } from '@/featureFlags/GlobalFeatureFlagsPanel';

import { AudioChunkSize } from './AudioChunkSize';
import { AppReviewRequestPlacement } from './AppReviewRequestPlacement';
import { BlockerContentUpdateForm } from './BlockerContentUpdateForm';
import { DefaultModelId } from './DefaultModelId';
import { DefaultTranscriptModelId } from './DefaultTranscriptModelId';
import { DiarizationEnrollmentSettings } from './DiarizationEnrollmentSettings';
import { FreeTrialPopupPlacement } from './FreeTrialPopupPlacement';
import { MandatoryOTAForm } from './MandatoryOTAForm';
import { SupportedNativeVersionRange } from './SupportedNativeVersionRange';
import { TracingSampleRates } from './TracingSampleRates';
import { TrialDurationUpdateForm } from './TrialDurationUpdateForm';
import { useGetAppConfig } from './useGetAppConfig';

const summarySx = {
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper': {
    transform: 'rotate(-90deg)',
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(0deg)',
  },
} as const;

const detailsSx = {
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  padding: '1rem',
} as const;

export const TherapistAppConfig = () => {
  const { remoteAppConfig } = useGetAppConfig();

  if (!remoteAppConfig) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        paddingTop="6rem"
        alignItems="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <SimpleShowLayout record={remoteAppConfig}>
      <Title title="Therapist App - Configuration" />

      {/* Feature Flags (registry-driven) */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
          <Typography variant="h6" sx={{ marginLeft: '1em' }}>
            Feature Flags
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={detailsSx}>
          <GlobalFeatureFlagsPanel />
        </AccordionDetails>
      </Accordion>

      {/* System Controls */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
          <Typography variant="h6" sx={{ marginLeft: '1em' }}>
            System Controls
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={detailsSx}>
          <MandatoryOTAForm />
          <SupportedNativeVersionRange />
        </AccordionDetails>
      </Accordion>

      {/* Trial & Onboarding */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
          <Typography variant="h6" sx={{ marginLeft: '1em' }}>
            Trial &amp; Onboarding
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={detailsSx}>
          <TrialDurationUpdateForm />
          <FreeTrialPopupPlacement />
          <AppReviewRequestPlacement />
        </AccordionDetails>
      </Accordion>

      {/* Models & defaults */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
          <Typography variant="h6" sx={{ marginLeft: '1em' }}>
            Models &amp; defaults
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={detailsSx}>
          <DefaultModelId />
          <DefaultTranscriptModelId />
          <AudioChunkSize />
        </AccordionDetails>
      </Accordion>

      {/* Transcription pipeline settings */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
          <Typography variant="h6" sx={{ marginLeft: '1em' }}>
            Transcription pipeline settings
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={detailsSx}>
          <DiarizationEnrollmentSettings />
        </AccordionDetails>
      </Accordion>

      {/* Sampling & tracing */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
          <Typography variant="h6" sx={{ marginLeft: '1em' }}>
            Sampling &amp; tracing
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={detailsSx}>
          <TracingSampleRates />
        </AccordionDetails>
      </Accordion>

      {/* Global Blocker */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
          <Typography variant="h6" sx={{ marginLeft: '1em' }}>
            Global Blocker
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={detailsSx}>
          <BlockerContentUpdateForm />
        </AccordionDetails>
      </Accordion>

      <Box height="10rem" />
    </SimpleShowLayout>
  );
};
